import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { StatusBadge, PageHeader } from "./bits";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Pencil, Trash2, MessageSquare } from "lucide-react";
import CommentsDialog from "./CommentsDialog";

export function ArtifactForm({ fields, value, onChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {fields.map((fld) => (
        <div key={fld.key} className={fld.type === "textarea" ? "sm:col-span-2" : ""}>
          <Label className="text-xs uppercase tracking-wide">{fld.label}</Label>
          {fld.type === "textarea" ? (
            <Textarea data-testid={`field-${fld.key}`} value={value[fld.key] ?? ""} rows={2}
              onChange={(e) => onChange({ ...value, [fld.key]: e.target.value })} />
          ) : fld.type === "select" ? (
            <Select value={String(value[fld.key] ?? "")} onValueChange={(v) => onChange({ ...value, [fld.key]: v })}>
              <SelectTrigger data-testid={`field-${fld.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {fld.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input data-testid={`field-${fld.key}`} type={fld.type === "number" ? "number" : fld.type === "date" ? "date" : "text"}
              value={value[fld.key] ?? ""}
              onChange={(e) => onChange({ ...value, [fld.key]: fld.type === "number" ? Number(e.target.value) : e.target.value })} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function ArtifactTable({ projectId, type, config }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [cell, setCell] = useState(null);
  const [commentsFor, setCommentsFor] = useState(null);

  const saveCell = async () => {
    if (!cell) return;
    const item = items.find((i) => i.id === cell.itemId);
    if (item && String(item[cell.key] ?? "") !== cell.value) {
      try {
        await api.put(`/projects/${projectId}/artifacts/${type}/${cell.itemId}`, { ...item, [cell.key]: cell.value });
        load();
      } catch (e) { toast.error(errMsg(e)); }
    }
    setCell(null);
  };

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/${type}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [projectId, type]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const startEdit = (item) => { setEditing(item); setForm(item); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/projects/${projectId}/artifacts/${type}/${editing.id}`, form);
      else await api.post(`/projects/${projectId}/artifacts/${type}`, form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const remove = async (item) => {
    try {
      await api.delete(`/projects/${projectId}/artifacts/${type}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={`artifact-page-${type}`}>
      <PageHeader overline={config.overline} title={config.title}
        actions={
          <Button data-testid={`add-${type}-btn`} onClick={startNew} className="rounded-none">
            <Plus className="h-4 w-4 mr-1" /> Add Entry
          </Button>
        }>
        <div className="text-xs text-muted-foreground mt-1">{items.length} entries · double-click a cell for inline edit</div>
      </PageHeader>

      <div className="border border-border bg-card overflow-auto max-h-[calc(100vh-220px)]">
        <table className="w-full data-table text-sm">
          <thead>
            <tr>
              {config.columns.map((c) => {
                const fld = config.fields.find((x) => x.key === c);
                return <th key={c}>{fld ? fld.label : c}</th>;
              })}
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={config.columns.length + 1} className="text-center text-muted-foreground py-8">No entries yet.</td></tr>
            )}
            {items.map((item) => (
              <tr key={item.id} data-testid={`row-${type}`}>
                {config.columns.map((c) => (
                  <td key={c} className={config.badges.includes(c) ? "whitespace-nowrap" : "cursor-text"}
                    onDoubleClick={() => !config.badges.includes(c) && setCell({ itemId: item.id, key: c, value: String(item[c] ?? "") })}>
                    {cell && cell.itemId === item.id && cell.key === c ? (
                      <input data-testid="inline-edit-input" autoFocus value={cell.value}
                        onChange={(e) => setCell({ ...cell, value: e.target.value })}
                        onBlur={saveCell} onKeyDown={(e) => { if (e.key === "Enter") saveCell(); if (e.key === "Escape") setCell(null); }}
                        className="w-full border border-[#1E6FE8] px-1 py-0.5 text-sm bg-white" />
                    ) : config.badges.includes(c) ? <StatusBadge value={item[c]} /> : <span title="Double-click to edit inline">{String(item[c] ?? "—")}</span>}
                  </td>
                ))}
                <td className="whitespace-nowrap">
                  <button data-testid={`comments-${type}-btn`} onClick={() => setCommentsFor(item)} className="p-1 hover:text-[#1E6FE8]"><MessageSquare className="h-3.5 w-3.5" /></button>
                  <button data-testid={`edit-${type}-btn`} onClick={() => startEdit(item)} className="p-1 hover:text-[#1E6FE8]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`delete-${type}-btn`} onClick={() => remove(item)} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CommentsDialog projectId={projectId} entityType={type} entityId={commentsFor?.id}
        entityLabel={commentsFor ? String(commentsFor[config.columns[0]] || config.title) : ""}
        open={!!commentsFor} onClose={() => setCommentsFor(null)} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} — {config.title}</DialogTitle></DialogHeader>
          <ArtifactForm fields={config.fields} value={form} onChange={setForm} />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid={`save-${type}-btn`} className="rounded-none" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
